import { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import type { Map as LeafletMap } from 'leaflet';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import { AiThemeSelector } from '../ai-explore/AiThemeSelector.js';
import type { AiExploreTheme } from '../ai-explore/types.js';

interface Props {
  mapRef: React.RefObject<LeafletMap | null>;
}

export function AiExploreButton({ mapRef }: Props) {
  const { latitude, longitude } = useGeolocationContext();
  const navigate = useNavigate();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showHint, setShowHint] = useState(false);

  const map = mapRef.current;
  const isLocationInView =
    latitude !== null &&
    longitude !== null &&
    map != null &&
    map.getBounds().contains([latitude, longitude]);

  const handlePress = () => {
    if (!isLocationInView) {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      setShowHint(true);
      hintTimerRef.current = setTimeout(() => setShowHint(false), 2500);
      return;
    }
    setSelectorOpen(true);
  };

  const handleThemeSelect = (theme: AiExploreTheme) => {
    setSelectorOpen(false);
    if (latitude === null || longitude === null || !map) return;

    const bounds = map.getBounds();
    const latDiff = bounds.getNorthEast().lat - bounds.getSouthWest().lat;
    const lngDiff = bounds.getNorthEast().lng - bounds.getSouthWest().lng;
    const radiusKm = Math.min(Math.max((Math.min(latDiff, lngDiff) * 111) / 2, 1), 10);

    void navigate(
      `/ai-spots?theme=${theme}&lat=${latitude.toFixed(6)}&lng=${longitude.toFixed(6)}&radiusKm=${radiusKm.toFixed(2)}`
    );
  };

  const hint =
    latitude === null || longitude === null
      ? '現在地を取得してから使ってください'
      : '現在地がマップ外です';

  return (
    <>
      {showHint && (
        <div className="absolute left-0 -top-10 bg-black/70 text-white text-xs rounded-lg px-3 py-1.5 whitespace-nowrap pointer-events-none">
          {hint}
        </div>
      )}

      {isLocationInView ? (
        <button
          type="button"
          onClick={handlePress}
          className="bg-white rounded-full px-3 py-1.5 shadow text-sm text-gray-600 active:bg-gray-100 flex items-center gap-1.5"
        >
          <span className="text-base leading-none">✨</span>
          AI探索
        </button>
      ) : (
        <button
          type="button"
          onClick={handlePress}
          className="bg-gray-200 text-gray-400 rounded-full px-3 py-1.5 shadow flex items-center gap-1.5 text-sm font-medium"
        >
          <span className="text-base leading-none">✨</span>
          AI探索
        </button>
      )}

      {selectorOpen && (
        <AiThemeSelector onSelect={handleThemeSelect} onClose={() => setSelectorOpen(false)} />
      )}
    </>
  );
}
